import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import PurchasedProductServices from "@/services/purchasedProductServices";
import RefundProcessServices from "@/services/refundProcessServices";
import { PurchasedProductWithRefundProcess } from "@/types/purchasedProduct";
import { getGlobalSessionId } from "@/services";

type PurchasedProductsState = {
  products: PurchasedProductWithRefundProcess[];
  setProducts: (_products: PurchasedProductWithRefundProcess[]) => void;
};

const usePurchasedProductsStore = create<PurchasedProductsState>((set) => ({
  products: [],
  setProducts: (products) => set({ products }),
}));

export const usePurchasedProducts = () => {
  const { setProducts } = usePurchasedProductsStore();
  const products = usePurchasedProductsStore((state) => state.products);

  const sessionId = getGlobalSessionId();

  const query = useQuery({
    queryKey: ["purchasedProducts"],
    enabled: !!sessionId,
    queryFn: async () => {
      try {
        // Fetch purchased products, refund processes, and lower price products
        const [purchasedProducts, refundProcesses, lowerPriceProducts] =
          await Promise.all([
            PurchasedProductServices.getPurchasedProducts(),
            RefundProcessServices.getRefundProcesses(),
            PurchasedProductServices.getUserLowerPriceProducts(),
          ]);

        // Create a map of refund processes for purchased products
        const purchasedProductRefundProcessMap = new Map();
        refundProcesses
          .filter(
            (p: any) =>
              p.type === "PURCHASED_PRODUCT" && p.referencePk && p.referenceSk
          )
          .forEach((process: any) => {
            const key = `${process.referencePk}#${process.referenceSk}`;
            purchasedProductRefundProcessMap.set(key, process);
          });

        // Create a map of lower price products
        const lowerPriceProductsMap = new Map();
        lowerPriceProducts.forEach((lowerPriceProduct: any) => {
          // Convert LOWERPRICE0# to PURCHASEDPROD0# to match with purchased products
          const purchasedProductPk = lowerPriceProduct.pk.replace(
            "LOWERPRICE0#",
            "PURCHASEDPROD0#"
          );
          const key = `${purchasedProductPk}#${lowerPriceProduct.sk}`;
          lowerPriceProductsMap.set(key, lowerPriceProduct);
        });

        // Map purchased products with refund process data and lower price products
        // Only include products that have a corresponding lower price product
        const productsWithRefund: PurchasedProductWithRefundProcess[] =
          purchasedProducts
            .map((product: any) => {
              const key = `${product.pk}#${product.sk}`;
              const process = purchasedProductRefundProcessMap.get(key);
              const lowerPriceProduct = lowerPriceProductsMap.get(key);
              return {
                ...product,
                currentStatus: process?.currentStatus || null,
                reasonForIntervention: process?.reason || null,
                refundProcessPk: process?.pk,
                refundProcessSk: process?.sk,
                lowerPriceProduct: lowerPriceProduct || null,
              };
            })
            .filter((product) => product.lowerPriceProduct !== null);

        setProducts(productsWithRefund);
        return productsWithRefund;
      } catch (error) {
        throw error;
      }
    },
  });

  return {
    products,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
